import type { RemoteProvider } from '../brain/types';
import {
  decryptCredential,
  deleteEncryptedCredential,
  encryptCredential,
  loadEncryptedCredential,
  MemoryCredentialVault,
  saveEncryptedCredential,
} from '../security/credentialVault';

export class CredentialDialog {
  private provider: RemoteProvider = 'openai';
  private readonly dialog = document.getElementById('credential-dialog') as HTMLDivElement;
  private readonly providerEl = document.getElementById('credential-provider') as HTMLSelectElement;
  private readonly keyEl = document.getElementById('credential-key') as HTMLInputElement;
  private readonly rememberEl = document.getElementById('credential-remember') as HTMLInputElement;
  private readonly passwordEl = document.getElementById('credential-password') as HTMLInputElement;
  private readonly messageEl = document.getElementById('credential-message') as HTMLParagraphElement;

  constructor(
    private readonly vault: MemoryCredentialVault,
    private readonly onReady: () => void,
  ) {
    document.getElementById('credential-save')!.addEventListener('click', () => void this.save());
    document.getElementById('credential-cancel')!.addEventListener('click', () => this.close());
    document.getElementById('credential-forget')!.addEventListener('click', () => void this.forget());
    this.providerEl.addEventListener('change', () => {
      this.provider = this.providerEl.value as RemoteProvider;
      this.resetFields();
    });
    this.rememberEl.addEventListener('change', () => this.updatePasswordVisibility());
  }

  open(provider: RemoteProvider): void {
    this.provider = provider;
    this.providerEl.value = provider;
    this.resetFields();
    this.dialog.classList.remove('hidden');
    this.keyEl.focus();
  }

  close(): void { this.dialog.classList.add('hidden'); }

  private resetFields(): void {
    this.keyEl.value = this.vault.get(this.provider) ?? '';
    this.passwordEl.value = '';
    this.messageEl.textContent = '默认仅在当前页面内存中保存。';
    this.updatePasswordVisibility();
  }

  private updatePasswordVisibility(): void {
    this.passwordEl.closest('.credential-password-row')?.classList.toggle('hidden', !this.rememberEl.checked);
  }

  private async save(): Promise<void> {
    try {
      let key = this.keyEl.value.trim();
      if (!key && this.rememberEl.checked) {
        const stored = await loadEncryptedCredential(this.provider);
        if (stored) key = await decryptCredential(stored, this.passwordEl.value);
      }
      if (!key) throw new Error('请输入 API Key');
      this.vault.set(this.provider, key);
      if (this.rememberEl.checked) {
        const encrypted = await encryptCredential(key, this.passwordEl.value);
        await saveEncryptedCredential(this.provider, encrypted);
      }
      this.keyEl.value = '';
      this.passwordEl.value = '';
      this.close();
      this.onReady();
    } catch (error) {
      this.messageEl.textContent = error instanceof Error ? error.message : '密钥保存失败';
    }
  }

  private async forget(): Promise<void> {
    this.vault.clear(this.provider);
    await deleteEncryptedCredential(this.provider);
    this.resetFields();
    this.messageEl.textContent = '该厂商密钥已清除。';
  }
}
